import { useEffect, useState, lazy, Suspense } from "react";
import { useNavigate, Link } from "react-router-dom";
import { Capacitor } from "@capacitor/core";
import { Browser } from "@capacitor/browser";
import { useAuth } from "@/hooks/useAuth";
import { Button } from "@/components/ui/button";
import { AlertTriangle, RefreshCw, Wifi, Instagram, Linkedin } from "lucide-react";
import { useNetworkStatus } from "@/hooks/useNetworkStatus";
import { useSEO } from "@/hooks/useSEO";
import { trackInstallSource, trackSignup, trackSignin, trackSplashView, trackAuthStart, trackHomeCtaClick } from "@/lib/analytics";
import { useToast } from "@/hooks/use-toast";
import { supabase } from "@/integrations/supabase/client";
import { SITE_URL, STORAGE_KEYS } from "@/config";
import OAuthButton from "@/components/auth/OAuthButton";
import LandingAppDownload from "@/components/landing/LandingAppDownload";
import LandingNav from "@/components/landing/LandingNav";
import LandingSplash from "@/components/landing/LandingSplash";
import LandingValueSignup from "@/components/landing/LandingValueSignup";
import LandingTopRated from "@/components/landing/LandingTopRated";
import LandingTestimonials from "@/components/landing/LandingTestimonials";
import { useVisitorLocation } from "@/data/queries/useVisitorLocation";
import { lazyWithRetry } from "@/lib/lazyWithRetry";

const FeedbackDialog = lazyWithRetry(() => import("@/components/FeedbackDialog").then(m => ({ default: m.FeedbackDialog })), "FeedbackDialog");
const BusinessContactDialog = lazyWithRetry(() => import("@/components/BusinessContactDialog").then(m => ({ default: m.BusinessContactDialog })), "BusinessContactDialog");

export interface SplashProps {
  /**
   * Dedicated auth entry pages (/signin, /signup) render this same landing
   * page with the auth block locked to one mode, so every future conversion
   * optimization on the homepage automatically applies to them too.
   */
  authMode?: "signIn" | "signUp";
}

const Splash = ({ authMode }: SplashProps = {}) => {
  const navigate = useNavigate();
  const { toast } = useToast();
  const { session, loading: authLoading, error, retryCount, retry, ready } = useAuth();
  const { hasConnection, isOnline } = useNetworkStatus();
  
  // Detect native platform
  const isNativeApp = Capacitor.isNativePlatform();
  
  // Auth form state
  const [mode, setMode] = useState<"signIn" | "signUp">(() => {
    if (authMode) return authMode;
    try {
      return localStorage.getItem(STORAGE_KEYS.HAS_LOGGED_IN_BEFORE) ? "signIn" : "signUp";
    } catch {
      return "signUp";
    }
  });
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [formLoading, setFormLoading] = useState(false);

  // IP-based (prompt-free) location used to prefill the nav search and the
  // "top rated" box. `topRatedSlug` lets the visitor override it.
  const { data: visitorLocation } = useVisitorLocation();
  const [topRatedSlug, setTopRatedSlug] = useState<string | null>(null);

  // SEO: Show meaningful content to crawlers.
  // The homepage and /auth share the "/" canonical; the dedicated auth pages
  // are self-canonical so they can be indexed and linked without duplication.
  const seo = authMode === "signIn"
    ? {
        title: "Sign in to BRRGR | Your burger ratings & journal",
        description: "Sign in to BRRGR with Google, Apple, or email to rate burgers, track your burger journal, and find the best burgers near you.",
        url: `${SITE_URL}/signin`,
      }
    : authMode === "signUp"
    ? {
        title: "Create your free BRRGR account | Rate the best burgers",
        description: "Create your free BRRGR account to rate burgers, build your journal, and find the best burgers near you.",
        url: `${SITE_URL}/signup`,
      }
    : {
        title: "BRRGR - Discover & Track the Best Burgers",
        description: "Find and rate the best burgers in your city. Join the burger community to discover top-rated burgers, share your experiences, and explore local favorites.",
        url: `${SITE_URL}/`,
      };

  useSEO({ ...seo, type: "website" });

  // Keep the auth block in sync when navigating between /signin and /signup
  // (React Router reuses this component across those routes).
  useEffect(() => {
    if (authMode) setMode(authMode);
  }, [authMode]);

  // Track install source and splash view on first load
  useEffect(() => {
    trackInstallSource();
    trackSplashView();
  }, []);

  // Redirect logic - only for logged-in users OR native apps (they have their own splash)
  useEffect(() => {
    if (authLoading || error) return;
    
    if (session) {
      // Logged-in users go to home
      try { localStorage.setItem(STORAGE_KEYS.HAS_LOGGED_IN_BEFORE, "true"); } catch {}
      navigate("/home", { replace: true });
    } else if (isNativeApp) {
      // Native app users who aren't logged in go to auth
      // (native apps have their own splash screen, so we redirect quickly)
      navigate("/auth", { replace: true });
    }
    // Web users who aren't logged in stay on this page (the landing page)
  }, [authLoading, session, navigate, error, isNativeApp]);

  // OAuth helpers
  const getOAuthRedirectUrl = () => {
    if (isNativeApp) return "com.brrgr.mobile://auth";
    return window.location.origin + "/home";
  };

  const openOAuthUrlNative = async (url: string) => {
    await Browser.open({ url, windowName: '_system' });
  };

  const handleOAuthSignIn = async (provider: "google" | "apple") => {
    if (!hasConnection) {
      toast({
        title: "No connection",
        description: "Please check your internet connection and try again.",
        variant: "destructive",
      });
      return;
    }

    try {
      setFormLoading(true);
      try { localStorage.removeItem(STORAGE_KEYS.GUEST); } catch {}
      trackAuthStart(provider);

      const { data, error } = await supabase.auth.signInWithOAuth({
        provider,
        options: {
          redirectTo: getOAuthRedirectUrl(),
          ...(isNativeApp ? { skipBrowserRedirect: true } : {}),
        },
      });

      if (error) throw error;

      if (isNativeApp) {
        const url = (data as any)?.url as string | undefined;
        if (!url) throw new Error("Missing OAuth URL");
        await openOAuthUrlNative(url);
      }

      trackSignin(provider);
    } catch (error: any) {
      const isNetworkError = error.message?.includes("fetch") || error.message?.includes("network");
      toast({
        title: `${provider === "apple" ? "Apple" : "Google"} sign-in failed`,
        description: isNetworkError
          ? `Unable to connect to ${provider === "apple" ? "Apple" : "Google"}. Please check your connection.`
          : error.message,
        variant: "destructive",
      });
    } finally {
      setFormLoading(false);
    }
  };

  const onSubmit = async (e: React.FormEvent) => {
    e.preventDefault();

    if (!hasConnection) {
      toast({ title: "No connection", description: "Please check your internet connection and try again.", variant: "destructive" });
      return;
    }

    try {
      setFormLoading(true);
      try { localStorage.removeItem(STORAGE_KEYS.GUEST); } catch {}
      trackAuthStart(mode === "signIn" ? 'email_signin' : 'email_signup');
      
      if (mode === "signIn") {
        const { error } = await supabase.auth.signInWithPassword({ email, password });
        if (error) throw error;
        trackSignin('email');
        try { localStorage.setItem(STORAGE_KEYS.HAS_LOGGED_IN_BEFORE, "true"); } catch {}
        navigate("/home", { replace: true });
      } else {
        const { error } = await supabase.auth.signUp({
          email,
          password,
          options: { emailRedirectTo: getOAuthRedirectUrl() }
        });
        if (error) throw error;
        trackSignup('email');
        toast({ title: "Account created!", description: "You're ready to sign in with your new credentials.", variant: "signup" as any });
        setMode("signIn");
      }
    } catch (err: any) {
      const isNetworkError = err.message?.includes('fetch') || err.message?.includes('network');
      toast({ 
        title: "Authentication error", 
        description: isNetworkError ? "Unable to connect to the server. Please check your connection and try again." : err?.message ?? "Something went wrong.", 
        variant: "destructive" 
      });
    } finally {
      setFormLoading(false);
    }
  };

  const sendMagicLink = async () => {
    if (!email) {
      toast({ title: "Email required", description: "Enter your email to receive a magic link." });
      return;
    }

    if (!hasConnection) {
      toast({ title: "No connection", description: "Please check your internet connection and try again.", variant: "destructive" });
      return;
    }

    try {
      setFormLoading(true);
      try { localStorage.removeItem(STORAGE_KEYS.GUEST); } catch {}
      trackAuthStart(mode === 'signUp' ? 'magic_link_signup' : 'magic_link');
      const { error } = await supabase.auth.signInWithOtp({
        email,
        options: { emailRedirectTo: getOAuthRedirectUrl() }
      });
      if (error) throw error;
      if (mode === 'signUp') trackSignup('magic_link'); else trackSignin('magic_link');
      toast({ title: "Check your email", description: "We sent you a magic sign-in link." });
    } catch (err: any) {
      const isNetworkError = err.message?.includes('fetch') || err.message?.includes('network');
      toast({ 
        title: "Magic link error", 
        description: isNetworkError ? "Unable to connect to the server. Please check your connection and try again." : err?.message ?? "Unable to send magic link.", 
        variant: "destructive" 
      });
    } finally {
      setFormLoading(false);
    }
  };

  const continueAsGuest = () => {
    trackHomeCtaClick('auth_continue_as_guest', '/home');
    try { localStorage.setItem(STORAGE_KEYS.GUEST, 'true'); } catch {}
    navigate('/home', { replace: true });
  };

  const handleRetry = async () => {
    await retry();
  };

  // Show error state
  if (error) {
    return (
      <main className="min-h-screen grid place-items-center bg-primary text-primary-foreground p-4">
        <section className="text-center max-w-md">
          <div className="mx-auto mb-6 h-16 w-16 rounded-full bg-destructive/10 flex items-center justify-center">
            {!isOnline ? (
              <Wifi className="h-8 w-8 text-destructive" />
            ) : (
              <AlertTriangle className="h-8 w-8 text-destructive" />
            )}
          </div>
          
          <h1 className="text-2xl font-bold mb-4">Connection Issue</h1>
          <p className="text-primary-foreground/80 mb-6 leading-relaxed">{error}</p>
          
          {retryCount > 0 && (
            <p className="text-sm text-primary-foreground/60 mb-4">Retry attempt: {retryCount}</p>
          )}
          
          <div className="space-y-3">
            <Button onClick={handleRetry} variant="secondary" className="w-full" disabled={authLoading}>
              <RefreshCw className={`mr-2 h-4 w-4 ${authLoading ? 'animate-spin' : ''}`} />
              {authLoading ? 'Retrying...' : 'Try Again'}
            </Button>
          </div>
        </section>
      </main>
    );
  }

  // Show loading state only for native apps (they redirect away quickly anyway)
  // Web users see the full landing page immediately to prevent CLS
  if (authLoading && isNativeApp) {
    return (
      <main className="min-h-screen grid place-items-center bg-primary text-primary-foreground">
        <section className="text-center max-w-lg px-4">
          <div className="mx-auto mb-8 h-24 w-24 rounded-full shadow-large overflow-hidden">
            <img
              src="/lovable-uploads/ece2f963-be6a-4c06-be17-e49ee5eb60b2.webp"
              alt="BRRGR logo"
              width={96}
              height={96}
              className="h-full w-full object-cover"
            />
          </div>
          <div className="flex items-center justify-center gap-1.5">
            <div className="h-2 w-2 rounded-full bg-primary-foreground/70 animate-bounce [animation-delay:0ms]" />
            <div className="h-2 w-2 rounded-full bg-primary-foreground/70 animate-bounce [animation-delay:150ms]" />
            <div className="h-2 w-2 rounded-full bg-primary-foreground/70 animate-bounce [animation-delay:300ms]" />
          </div>
          
          <div className="sr-only">
            <h2>Find the Best Burgers Near You</h2>
            <p>BRRGR is your ultimate burger discovery app. Rate burgers, follow fellow burger lovers, and explore top-rated burgers in cities around the world.</p>
          </div>
          
          {!hasConnection && (
            <p className="mt-2 text-primary-foreground/80">Connecting to network...</p>
          )}
        </section>
      </main>
    );
  }

  const scrollToAuth = () => {
    document.getElementById('signup')?.scrollIntoView({ behavior: 'smooth', block: 'center' });
  };

  const locationSlug = visitorLocation?.slug ?? 'world';

  // Web landing page - full content for SEO and users
  return (
    <main className="min-h-screen bg-background text-foreground">
      <LandingNav
        locationSlug={topRatedSlug ?? locationSlug}
        onCreateAccount={() => { setMode('signUp'); scrollToAuth(); }}
        onSignIn={() => { setMode('signIn'); scrollToAuth(); }}
      />

      <LandingSplash hideClutter={!!authMode}>
        {/* ============ SIGNUP (over the hero, above the fold) ============ */}
        <LandingValueSignup
          mode={mode}
          setMode={setMode}
          email={email}
          setEmail={setEmail}
          password={password}
          setPassword={setPassword}
          formLoading={formLoading}
          hasConnection={hasConnection}
          isOnline={isOnline}
          onOAuth={handleOAuthSignIn}
          onPasswordSubmit={onSubmit}
          onMagicLink={sendMagicLink}
          onGuest={continueAsGuest}
        />
      </LandingSplash>

      <section className="w-full max-w-6xl mx-auto px-6 md:px-8 pt-10 md:pt-12 pb-10">
        {/* ============ APP DOWNLOAD ROW ============ */}
        <LandingAppDownload />


        {/* ============ TESTIMONIALS (social proof) ============ */}
        <LandingTestimonials />

        {/* ============ TOP RATED (location aware) ============ */}
        <LandingTopRated
          locationSlug={topRatedSlug ?? locationSlug}
          onLocationChange={setTopRatedSlug}
        />




        {/* Footer links */}
        <footer className="mt-12 flex flex-col items-center text-muted-foreground text-sm" role="contentinfo">
          <div className="flex gap-4 items-center mb-4">
            <a
              href="https://instagram.com/brrgr.app"
              target="_blank"
              rel="noopener noreferrer"
              className="hover:text-primary-hover transition-colors"
              aria-label="Follow BRRGR on Instagram"
              onClick={() => trackHomeCtaClick('footer_instagram', 'https://instagram.com/brrgr.app')}
            >
              <Instagram className="h-5 w-5" />
            </a>
            <a
              href="https://linkedin.com/company/brrgr"
              target="_blank"
              rel="noopener noreferrer"
              className="hover:text-primary-hover transition-colors"
              aria-label="Follow BRRGR on LinkedIn"
              onClick={() => trackHomeCtaClick('footer_linkedin', 'https://linkedin.com/company/brrgr')}
            >
              <Linkedin className="h-5 w-5" />
            </a>
          </div>
          <nav aria-label="Footer navigation" className="flex flex-wrap justify-center gap-x-4 gap-y-1">
            <Link to="/leaderboards" className="underline underline-offset-4 hover:text-primary-hover transition-colors" onClick={() => trackHomeCtaClick('footer_leaderboards', '/leaderboards')}>Best burgers by location</Link>
            <span aria-hidden="true">·</span>
            <Link to="/signup" className="underline underline-offset-4 hover:text-primary-hover transition-colors" onClick={() => trackHomeCtaClick('footer_signup', '/signup')}>Create account</Link>
            <span aria-hidden="true">·</span>
            <Link to="/signin" className="underline underline-offset-4 hover:text-primary-hover transition-colors" onClick={() => trackHomeCtaClick('footer_signin', '/signin')}>Sign in</Link>
            <span aria-hidden="true">·</span>
            <Link to="/terms" className="underline underline-offset-4 hover:text-primary-hover transition-colors" onClick={() => trackHomeCtaClick('footer_terms', '/terms')}>Terms</Link>
            <span aria-hidden="true">·</span>
            <Link to="/privacy" className="underline underline-offset-4 hover:text-primary-hover transition-colors" onClick={() => trackHomeCtaClick('footer_privacy', '/privacy')}>Privacy</Link>
            <span aria-hidden="true">·</span>
            <Suspense fallback={null}>
              <FeedbackDialog
                trigger={
                  <button
                    className="underline underline-offset-4 hover:text-primary-hover transition-colors"
                    onClick={() => trackHomeCtaClick('footer_feedback', 'dialog')}
                  >
                    Feedback
                  </button>
                }
              />
            </Suspense>
          </nav>
          <div className="mt-3">
            <Suspense fallback={null}>
              <BusinessContactDialog
                trigger={
                  <button
                    className="underline underline-offset-4 hover:text-primary-hover transition-colors"
                    onClick={() => trackHomeCtaClick('footer_business_contact', 'dialog')}
                  >
                    Business owner? Get in touch
                  </button>
                }
              />
            </Suspense>
          </div>
        </footer>
      </section>
    </main>
  );
};

export default Splash;
